import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAppStore } from '@store/appStore';
import { useAuthStore } from '@store/authStore';
import { apiClient } from '@/lib/api';
import useQuoteForm from '../hooks/useQuoteForm';
import useQuoteProcessor from '../hooks/useQuoteProcessor';
import { useAirports } from '../services/airportService';
import { mapAircraftToPayload } from '../utils/transformBidData';
import { getQuoteStatusConfig } from '../utils';
import { Skeleton } from '@components/ui/Skeleton';
import Modal from '@components/ui/Modal';
import { RiLoader4Line, RiErrorWarningLine } from '@remixicon/react';
import ClientInformationSection from '../components/generate/ClientInformationSection';
import FlightDetailsSection from '../components/generate/FlightDetailsSection';
import AircraftOptionsSection from '../components/generate/AircraftOptionsSection';
import QuoteSettingsSection from '../components/generate/QuoteSettingsSection';
import QuotePreview from '../components/generate/QuotePreview';
import QuoteCustomizeModal from '../components/generate/QuoteCustomizeModal';
import { resolveAssetUrl } from '@/utils';

/**
 * GenerateQuote Page
 * Main page for generating and sending quote quotes
 */
const GenerateQuote = () => {
  const [searchParams] = useSearchParams();
  const isQuickQuote = searchParams.has('quickquote');
  const editQuoteId = searchParams.get('quoteid');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [customizeModalOpen, setCustomizeModalOpen] = useState(false);
  // Tracks the last submit action so the error-banner retry continues the same
  // flow: 'draft' → re-run the draft save; 'send' → reopen the email composer.
  const [lastSubmitMode, setLastSubmitMode] = useState('send');
  // Edit-mode Send confirmation: asks whether to resend the email to the
  // client (open the composer) or just save silently (PATCH only).
  const [editSendConfirmOpen, setEditSendConfirmOpen] = useState(false);
  // Themed validation modal: surfaces missing/invalid fields and jumps to
  // the first failing section instead of silently dropping an inline error.
  const [validationModal, setValidationModal] = useState({ open: false, issues: [] });
  const { shortlistAircraft, selectedTripGroup, clearShortlist, clearSelectedTripGroup } = useAppStore();
  const { userProfile } = useAuthStore();

  const { data: organization } = useQuery({
    queryKey: ['organization', userProfile?.org_id],
    queryFn: () => apiClient.getOrganizationById(userProfile.org_id),
    enabled: !!userProfile?.org_id,
  });

  // Edit mode: fetch the source quote when ?quoteid= is present, then
  // hydrate the form below.
  const { data: editSource } = useQuery({
    queryKey: ['quoteDetails', editQuoteId],
    queryFn: () => apiClient.getQuoteById(editQuoteId),
    enabled: !!editQuoteId,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  // Cached airport list — used to enrich each leg's airport object during
  // hydration so LocationInput shows "KOPF - Miami-Opa Locka Exec" instead
  // of just the bare ICAO code.
  const { data: airports } = useAirports();

  const {
    formData,
    updateClient,
    updateFlights,
    updateSettings,
    updateBidOptions,
    updateBidOptionProperty,
    updateBidOptionProperties,
    removeBidOption,
    toggleSection,
    goToNextSection,
    goToPreviousSection,
    expandedSections,
    unlockedSections,
    completedSections,
    resetForm,
    getFormErrors,
    getValidationIssues,
    openSection,
    loadFromQuote,
    isEditing,
  } = useQuoteForm();

  const {
    handleSaveDraft,
    handleSendQuote,
    handleUpdateQuote,
    handleUpdateQuoteAndNotify,
    isSending,
    isSavingDraft,
    error,
    setError,
  } = useQuoteProcessor(organization);

  // Hydrate the form once the edit source AND the airports cache are ready.
  // Waiting for airports lets us enrich each leg's airport object so the
  // LocationInput renders the full "ICAO - Name" pair rather than just the code.
  useEffect(() => {
    if (editSource && airports?.length) {
      loadFromQuote(editSource, { airports });
    }
  }, [editSource, airports, loadFromQuote]);

  // Lock the Client Information section in edit mode once the quote has been
  // sent to the client — changing the client mid-flow would orphan the
  // already-delivered email and confuse the recipient.
  const isClientEditLocked = (() => {
    const status = (editSource?.status || '').toUpperCase();
    return isEditing && ['SENT', 'VIEWED', 'SIGNED'].includes(status);
  })();
  // TEMP: surface the lock decision to make miswiring easy to spot.
  if (editSource) {
    // eslint-disable-next-line no-console
    console.log('[GenerateQuote] edit lock check', {
      isEditing,
      sourceStatus: editSource?.status,
      isClientEditLocked,
    });
  }

  const isDataLoading = !shortlistAircraft && !selectedTripGroup;

  // Memoize allBidOptions to prevent unnecessary recalculations
  const allBidOptions = React.useMemo(() => {
    if (shortlistAircraft && shortlistAircraft.length > 0) {
      return shortlistAircraft.map(mapAircraftToPayload);
    }
    return selectedTripGroup?.bids?.flatMap(bid => bid.bid_options || []) || [];
  }, [shortlistAircraft, selectedTripGroup]);

  // Reset form when component mounts or navigation changes.
  // Skipped in edit mode — the form must hydrate from the source quote
  // (handled by the loadFromQuote effect above), not be cleared.
  useEffect(() => {
    if (editQuoteId) return;
    if (isQuickQuote) {
      clearShortlist();
      clearSelectedTripGroup();
      resetForm();
    } else {
      resetForm({ preserveBidOptions: !!selectedTripGroup });
    }
  }, [searchParams]);

  // Initialize and update bidOptions when shortlistAircraft changes.
  // Skipped in edit mode — bid options come from loadFromQuote, and
  // the shortlist isn't relevant when editing an existing quote.
  useEffect(() => {
    if (editQuoteId) return;
    if (allBidOptions.length > 0 && !isDataLoading) {
      updateBidOptions(prev => {
        if (!prev || prev.length === 0) return allBidOptions;
        const existingMap = new Map(prev.map(opt => [opt.bid_option_id, opt]));
        return allBidOptions.map(opt => {
          const existing = existingMap.get(opt.bid_option_id);
          return existing ? { ...opt, ...existing } : opt;
        });
      });
    }
  }, [allBidOptions, isDataLoading, updateBidOptions, editQuoteId]);

  // Open preview (validates form first).
  // Uses `getValidationIssues` which re-runs the real validators (bypasses
  // the `manuallyCompleted` UX shortcut). If anything fails, surface a
  // themed modal that lists the issues and offers a one-click jump to the
  // first failing section.
  const handleOpenPreview = () => {
    const issues = getValidationIssues();
    if (issues.length > 0) {
      setValidationModal({ open: true, issues });
      return;
    }
    // Legacy guard kept for safety — should be a no-op now.
    const errors = getFormErrors();
    const firstError = Object.values(errors)[0];
    if (firstError) {
      setError(firstError || 'Please complete all sections before previewing');
      setTimeout(() => setError(null), 5000);
      return;
    }
    setPreviewOpen(true);
  };

  // Modal action: close + force-open the failing section so the broker can
  // fix the field immediately. Scrolling into view is handled by the
  // section's own collapse/expand animation.
  const handleJumpToSection = (section) => {
    setValidationModal({ open: false, issues: [] });
    if (section) openSection(section);
  };

  // Handle customize modal confirm - update settings and send quote
  const handleCustomizeConfirm = (customizedData) => {
    const updatedSettings = {
      ...formData.settings,
      ...customizedData,
    };
    updateSettings(updatedSettings);
    setCustomizeModalOpen(false);
    setPreviewOpen(true);

    const quoteData = {
      ...formData,
      settings: updatedSettings,
      flights: formData.flights.flights,
      flightType: formData.flights.type,
    };
    setLastSubmitMode('send');
    // In edit mode, route to the PATCH-and-notify path so we update the
    // existing quote instead of creating a duplicate.
    if (isEditing) {
      handleUpdateQuoteAndNotify(quoteData);
    } else {
      handleSendQuote(quoteData);
    }
  };

  // Wrap handlers to also close preview. In edit mode, "Save Draft" must
  // PATCH the existing quote — calling `handleSaveDraft` would POST a new
  // /quotes record and orphan the original. `handleUpdateQuote` preserves
  // the current status (DRAFT stays DRAFT) and does the silent save.
  const onSaveDraft = async (quoteData) => {
    setLastSubmitMode('draft');
    if (isEditing) {
      await handleUpdateQuote(quoteData);
    } else {
      await handleSaveDraft(quoteData);
    }
    setPreviewOpen(false);
  };

  // Edit-mode submit. The preview's "Send" button routes here instead of
  // opening the customize modal — edits commit in place, no resend dialog.
  const onSaveEdit = async () => {
    const quoteData = {
      ...formData,
      flights: formData.flights.flights,
      flightType: formData.flights.type,
    };
    await handleUpdateQuote(quoteData);
    setPreviewOpen(false);
  };


  return (
    <div className="generate-quote-page pb-24 w-full lg:max-w-[1000px] bg-white/50 backdrop-blur-sm rounded-[2rem] p-4 md:p-6">
      <main className="px-1 py-3">
        {isEditing && editSource?.quote_number && (
          <div className="flex items-center justify-between gap-2 mb-4">
            <h4 className="text-gray-500 bg-white rounded-lg px-3 py-1">
              Editing Quote: <span className="text-primary-500 font-semibold">{editSource.quote_number}</span>
            </h4>
            {editSource?.status && (() => {
              const config = getQuoteStatusConfig(editSource.status);
              const Icon = config.icon;
              return (
                <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md ${config.className}`}>
                  {Icon && <Icon className="w-3.5 h-3.5" />}
                  {config.label}
                </span>
              );
            })()}
          </div>
        )}
        {!isEditing && selectedTripGroup?.tripNumber &&
          <div className="text-right mb-4">
            <h4 className="text-gray-500 inline bg-white rounded-lg px-3 py-1">Trip: <span className="text-primary-500 font-semibold">{selectedTripGroup.tripNumber}</span></h4>
          </div>}
        {/* Edit-mode loading gate. Blocks the form while we either fetch the
            source quote OR wait for the airports cache to be available for
            enrichment. `isEditing` flips true only after `loadFromQuote` has
            populated formData. */}
        {editQuoteId && !isEditing ? (
          <div className="space-y-4" aria-busy="true" aria-live="polite">
            <div className="flex items-center justify-center gap-2 py-4 text-sm text-gray-600">
              <RiLoader4Line className="w-4 h-4 animate-spin text-primary-600" />
              <span>Loading quote…</span>
            </div>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 space-y-3">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-5 w-5 rounded" />
                  <Skeleton className="h-5 w-40" />
                </div>
                <Skeleton className="h-10 w-full rounded-lg" />
                <div className="grid grid-cols-2 gap-3">
                  <Skeleton className="h-10 w-full rounded-lg" />
                  <Skeleton className="h-10 w-full rounded-lg" />
                </div>
              </div>
            ))}
          </div>
        ) : (
        <div className="space-y-4">
          {error && (
            <div
              role="alert"
              className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg"
            >
              <p className="text-sm font-medium">
                {isEditing
                  ? 'Unable to save changes to this quote at this time. This may be a temporary system issue. Please retry, and contact support if it continues.'
                  : lastSubmitMode === 'draft'
                    ? 'Unable to save this draft at this time. This may be a temporary system issue. Please retry, and contact support if it continues.'
                    : 'Unable to generate a new quote at this time. This may be due to a temporary system issue. Please retry resending quote, and contact support if it continues.'}
              </p>

            <button
            type="button"
            onClick={() => {
              if (isEditing) {
                // Retry the silent PATCH path. The broker can still trigger
                // a re-send via the preview's "Send Quote to Client" button
                // if they want the client to receive the revised proposal.
                onSaveEdit();
              } else if (lastSubmitMode === 'draft') {
                // Continue the draft save. The processor resumes from the failed
                // step (it won't re-create the bid / bid_options).
                const quoteData = {
                  ...formData,
                  flights: formData.flights.flights,
                  flightType: formData.flights.type,
                };
                onSaveDraft(quoteData);
              } else {
                setCustomizeModalOpen(true);
              }
            }}
            className="px-5 py-1 mt-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-medium"
          >
            {isEditing || lastSubmitMode === 'draft' ? 'Try Again' : 'Resend Quote'}
          </button>
            </div>
          )}

          <ClientInformationSection
            data={formData.client}
            onUpdate={updateClient}
            isComplete={completedSections.client}
            isExpanded={expandedSections.client}
            onToggle={() => toggleSection('client')}
            onNext={() => goToNextSection('client')}
            locked={isClientEditLocked}
            lockedReason="The quote has already been sent — the client cannot be changed."
          />

          <FlightDetailsSection
            data={formData.flights}
            onUpdate={updateFlights}
            isComplete={completedSections.flights}
            isExpanded={expandedSections.flights}
            onToggle={() => toggleSection('flights')}
            onNext={() => goToNextSection('flights')}
            onBack={() => goToPreviousSection('flights')}
            isClientComplete={completedSections.client}
            isUnlocked={unlockedSections.flights}
          />

          <AircraftOptionsSection
            formData={formData}
            isComplete={completedSections.aircraft}
            isExpanded={expandedSections.aircraft}
            onToggle={() => toggleSection('aircraft')}
            onNext={() => goToNextSection('aircraft')}
            onBack={() => goToPreviousSection('aircraft')}
            isUnlocked={unlockedSections.aircraft}
            onUpdate={updateBidOptionProperty}
            onBatchUpdate={updateBidOptionProperties}
            onRemove={removeBidOption}
            isDataLoading={isDataLoading}
          />

          <QuoteSettingsSection
            settings={formData.settings}
            onUpdate={updateSettings}
            isComplete={completedSections.settings}
            isExpanded={expandedSections.settings}
            onToggle={() => toggleSection('settings')}
            onPreview={handleOpenPreview}
            onBack={() => goToPreviousSection('settings')}
            isAircraftComplete={completedSections.aircraft}
            isUnlocked={unlockedSections.settings}
            flightOptions={formData.bidOptions}
            defaultContractUrl={resolveAssetUrl(organization?.branding?.contract)}
          />
        </div>
        )}
      </main>

      <QuoteCustomizeModal
        isOpen={customizeModalOpen}
        onClose={() => setCustomizeModalOpen(false)}
        settings={formData.settings}
        clientData={formData.client?.clientData}
        onConfirm={handleCustomizeConfirm}
        bidOptions={formData.bidOptions}
      />

      <QuotePreview
        isOpen={previewOpen}
        onClose={() => !isSending && !isSavingDraft && setPreviewOpen(false)}
        quoteData={{
          ...formData,
          flights: formData.flights.flights,
          flightType: formData.flights.type
        }}
        onSend={isEditing
          ? () => {
              // DRAFT in edit mode = first-time send, skip the "Resend?" prompt
              // and go straight to the customize/email composer.
              const sourceStatus = (editSource?.status || '').toUpperCase();
              if (sourceStatus === 'DRAFT' || !sourceStatus) {
                // Close the preview drawer before opening the customize modal —
                // the drawer's focus trap would otherwise block the modal's
                // subject/body inputs from receiving focus (not editable).
                setPreviewOpen(false);
                setCustomizeModalOpen(true);
              } else {
                setEditSendConfirmOpen(true);
              }
            }
          : () => { setPreviewOpen(false); setCustomizeModalOpen(true); }}
        onSaveDraft={onSaveDraft}
        isSending={isSending}
        isSavingDraft={isSavingDraft}
        isEditing={isEditing}
        hideSaveDraft={isEditing && (editSource?.status || '').toUpperCase() !== 'DRAFT'}
      />

      {/* Edit-mode Send confirmation. Broker chooses whether to push the
          revised quote to the client (opens the customize/email composer
          and PATCHes via handleUpdateQuoteAndNotify), or just save silently
          (PATCH only via onSaveEdit). */}
      <Modal
        isOpen={editSendConfirmOpen}
        onClose={() => !isSending && setEditSendConfirmOpen(false)}
        title="Resend the updated quote?"
        size="sm"
        closeOnOutsideClick={!isSending}
      >
        <div className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center">
              {isSending
                ? <RiLoader4Line className="w-5 h-5 text-primary-600 animate-spin" />
                : <RiErrorWarningLine className="w-5 h-5 text-primary-600" />}
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">
              Do you want to <span className="font-semibold">resend the email</span> to
              the client with the updated quote, or <span className="font-semibold">just save</span>
              {' '}the changes silently?
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setEditSendConfirmOpen(false)}
              disabled={isSending}
              className="px-4 py-2 text-sm text-gray-700 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={async () => {
                setEditSendConfirmOpen(false);
                await onSaveEdit();
              }}
              disabled={isSending}
              className="px-4 py-2 text-sm text-gray-700 rounded-full border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Just save
            </button>
            <button
              type="button"
              onClick={() => {
                setEditSendConfirmOpen(false);
                // Close the preview drawer so its focus trap doesn't block the
                // customize modal's subject/body inputs.
                setPreviewOpen(false);
                setCustomizeModalOpen(true);
              }}
              disabled={isSending}
              className="px-4 py-2 text-sm text-white bg-primary-700 rounded-full hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Resend email
            </button>
          </div>
        </div>
      </Modal>

      {/* Validation modal — opens when the broker clicks Preview but a section
          still has missing/invalid fields. Lists each issue and offers a
          one-click jump to the first failing section. */}
      <Modal
        isOpen={validationModal.open}
        onClose={() => setValidationModal({ open: false, issues: [] })}
        title="Some fields need updating"
        size="sm"
      >
        <div className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center">
              <RiErrorWarningLine className="w-5 h-5 text-amber-600" />
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">
              The following needs to be updated before you can preview this quote:
            </p>
          </div>

          <ul className="space-y-3 ml-13">
            {validationModal.issues.map((issue) => (
              <li
                key={issue.section}
                className="border border-gray-200 rounded-xl p-3 bg-gray-50"
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-sm font-semibold text-gray-900">{issue.label}</span>
                  <button
                    type="button"
                    onClick={() => handleJumpToSection(issue.section)}
                    className="text-xs px-3 py-1 rounded-full bg-primary-700 text-white hover:bg-primary-800 transition-colors"
                  >
                    Open
                  </button>
                </div>
                <ul className="text-xs text-gray-600 list-disc pl-4 space-y-0.5">
                  {issue.messages.map((m, i) => (
                    <li key={i}>{m}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setValidationModal({ open: false, issues: [] })}
              className="px-4 py-2 text-sm text-gray-700 rounded-full hover:bg-gray-100"
            >
              Close
            </button>
            {validationModal.issues[0] && (
              <button
                type="button"
                onClick={() => handleJumpToSection(validationModal.issues[0].section)}
                className="px-4 py-2 text-sm text-white bg-primary-700 rounded-full hover:bg-primary-800"
              >
                Fix {validationModal.issues[0].label}
              </button>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default GenerateQuote;
